import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { apiFetch } from "../utils/apifetch";
import "./Judge.css";

//const API = import.meta.env.VITE_API_URL;

const SEARCH_DEBOUNCE_MS = 300;

const emptyForm = {
  id: null,
  judgeTitle: "",
  judgeName: "",
  judgeInitial: "",
  activeStatus: "active",
};

export default function Judge() {
  const navigate = useNavigate();
  const { logout, hasPermission } = useAuth();

  const [judges, setJudges] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");

  /* ================= SEARCH (DEBOUNCED) ================= */
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  /* ================= SORT ================= */
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  /* ================= FORM ================= */
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const isEdit = !!form.id;

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  /* ================= DEBOUNCE EFFECT ================= */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [searchInput]);

  /* ================= LOAD DATA ================= */
  const loadJudges = async () => {
    try {
      const res = await apiFetch(
        `/judges`,
        { headers: authHeader() },
        logout,
        navigate,
      );
      setJudges(await res.json());
    } catch (err) {
      setError(err.message || "Unable to load judges");
    }
  };

  const loadSummary = async () => {
    try {
      const res = await apiFetch(
        `/judges/summary`,
        { headers: authHeader() },
        logout,
        navigate,
      );
      if (res.ok) setSummary(await res.json());
    } catch {}
  };

  useEffect(() => {
    Promise.all([loadJudges(), loadSummary()]).finally(() => setLoading(false));
  }, []);

  /* ================= SORT HANDLER ================= */
  const handleSort = (key) => {
    setSortDir((prev) =>
      sortKey === key ? (prev === "asc" ? "desc" : "asc") : "asc",
    );
    setSortKey(key);
  };

  /* ================= FILTER + SORT ================= */
  const filteredJudges = useMemo(() => {
    const q = search.toLowerCase();

    let data = judges.filter((j) => {
      return (
        j.judgeName?.toLowerCase().includes(q) ||
        j.judgeTitle?.toLowerCase().includes(q) ||
        j.judgeInitial?.toLowerCase().includes(q) ||
        j.activeStatus?.toLowerCase().includes(q)
      );
    });

    if (sortKey) {
      data = [...data].sort((a, b) => {
        const x = (a[sortKey] ?? "").toString().toLowerCase();
        const y = (b[sortKey] ?? "").toString().toLowerCase();
        if (x < y) return sortDir === "asc" ? -1 : 1;
        if (x > y) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [judges, search, sortKey, sortDir]);

  /* ================= FORM HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddJudge = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(true);
  };

  const handleEditJudge = (j) => {
    setForm({
      id: j.id,
      judgeTitle: j.judgeTitle,
      judgeName: j.judgeName,
      judgeInitial: j.judgeInitial,
      activeStatus: j.activeStatus,
    });
    setFormError("");
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(false);
  };

  const submit = async () => {
    const method = isEdit ? "PUT" : "POST";
    const url = isEdit ? `/judges/${form.id}` : `/judges`;

    const payload = {
      judgeTitle: form.judgeTitle,
      judgeName: form.judgeName,
      judgeInitial: form.judgeInitial,
      activeStatus: form.activeStatus,
      createdByUid: localStorage.getItem("uid"),
    };

    try {
      const res = await apiFetch(
        url,
        {
          method,
          headers: authHeader(),
          body: JSON.stringify(payload),
        },
        logout,
        navigate,
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      resetForm();
      loadJudges();
      loadSummary();
    } catch (err) {
      setFormError(err.message || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this judge?")) return;

    await apiFetch(
      `/judges/${id}`,
      { method: "DELETE", headers: authHeader() },
      logout,
      navigate,
    );

    loadJudges();
    loadSummary();
  };

  /* ================= UI ================= */
  return (
    <div className="judge-page">
      <h2>List of Judges</h2>

      {/* ===== SUMMARY ===== */}
      <div className="judge-cards">
        <div className="judge-card total">
          Total Judges <span>{summary.total}</span>
        </div>
        <div className="judge-card active">
          Active Judges <span>{summary.active}</span>
        </div>
        <div className="judge-card inactive">
          Inactive Judges <span>{summary.inactive}</span>
        </div>
      </div>

      {/* ===== TOOLBAR ===== */}
      <div className="judge-filters">
        <input
          placeholder="Search by Name, Title, or Initial"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <button
          className="add-judge-btn"
          onClick={handleAddJudge}
          disabled={!hasPermission("judge", "create")}
        >
          + Add Judge
        </button>
      </div>
      {/* ===== INLINE FORM ===== */}
      {showForm && (
        <div className="judge-inline-form">
          <input
            name="judgeTitle"
            placeholder="Judge Title"
            value={form.judgeTitle}
            onChange={handleChange}
          />
          <input
            name="judgeName"
            placeholder="Judge Name"
            value={form.judgeName}
            onChange={handleChange}
          />
          <input
            name="judgeInitial"
            placeholder="Judge Initial"
            value={form.judgeInitial}
            onChange={handleChange}
          />
          <select
            name="activeStatus"
            value={form.activeStatus}
            onChange={handleChange}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button className="save-btn" onClick={submit}>
            Save
          </button>
          <button className="cancel-btn" onClick={resetForm}>
            Cancel
          </button>
        </div>
      )}

      {/* ===== TABLE ===== */}
      {!loading && (
        <table className="judge-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("judgeTitle")}>
                Title
                <span className="sort-icons">▲▼</span>
              </th>
              <th onClick={() => handleSort("judgeInitial")}>
                Initial
                <span className="sort-icons">▲▼</span>
              </th>
              <th onClick={() => handleSort("judgeName")}>
                Name
                <span className="sort-icons">▲▼</span>
              </th>
              <th onClick={() => handleSort("activeStatus")}>
                Status
                <span className="sort-icons">▲▼</span>
              </th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredJudges.map((j) => (
              <tr key={j.id}>
                <td>{j.judgeTitle}</td>
                <td>{j.judgeInitial}</td>
                <td>{j.judgeName}</td>
                <td>
                  <span className={`status ${j.activeStatus}`}>
                    {j.activeStatus}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => handleEditJudge(j)}
                      disabled={!hasPermission("judge", "update")}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => del(j.id)}
                      disabled={!hasPermission("judge", "delete")}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!filteredJudges.length && (
              <tr>
                <td colSpan="5" className="no-data">
                  No judges found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {error && <p className="form-error">{error}</p>}
      {formError && <p className="form-error">{formError}</p>}
    </div>
  );
}
